import {
  cloneInstrument,
  cloneSamplerPreset,
  type Instrument,
  type InstrumentSlot,
  type SamplerPreset,
  samplerInstrument,
} from "../../../../src/lib/audio";
import {
  isSamplerPresetShape,
  newSamplerPresetId,
  sanitizeSamplerBank,
  sanitizeSamplerPresetCategory,
  sanitizeSamplerPresetTags,
  samplerCategoryLabel,
  type NamedSamplerPreset,
  type SamplerPresetCategoryId,
} from "./samplerBank";

export type SamplerBankState = {
  instruments: Instrument[];
  bank: NamedSamplerPreset[];
  slotBankIds: Array<string | undefined>;
};

export type SamplerBankResult = SamplerBankState & {
  status: string;
  changedSlots: InstrumentSlot[];
};

function cloneState(state: SamplerBankState): SamplerBankState {
  return {
    instruments: state.instruments.map(cloneInstrument),
    bank: sanitizeSamplerBank(state.bank),
    slotBankIds: [...state.slotBankIds],
  };
}

function slotPreset(state: SamplerBankState, slot: InstrumentSlot): SamplerPreset | null {
  const inst = state.instruments[slot];
  return inst?.kind === "sampler" ? inst.preset : null;
}

export function initialSamplerSlotBankIds(slotCount: number): Array<string | undefined> {
  return Array.from({ length: slotCount }, () => undefined);
}

export function syncSamplerSlotBankIdsFromInstruments(state: SamplerBankState): SamplerBankState {
  const next = cloneState(state);
  for (let i = 0; i < next.instruments.length; i++) {
    const inst = next.instruments[i];
    if (inst?.kind !== "sampler") {
      continue;
    }
    const match = next.bank.find(entry => entry.name === inst.preset.name);
    if (match) {
      next.slotBankIds[i] = match.id;
    }
  }
  return next;
}

export function applySamplerBankEntry(
  state: SamplerBankState,
  slot: InstrumentSlot,
  bankId: string,
): SamplerBankResult | null {
  const entry = state.bank.find(item => item.id === bankId);
  if (!entry) {
    return null;
  }
  const next = cloneState(state);
  next.instruments[slot] = samplerInstrument({ ...entry.preset, name: entry.name });
  next.slotBankIds[slot] = entry.id;
  return {
    ...next,
    status: `Loaded “${entry.name}” into slot ${slot + 1}`,
    changedSlots: [slot],
  };
}

/** Returns null when there is no bank entry to overwrite — caller should run Save as. */
export function saveSamplerBankCurrent(
  state: SamplerBankState,
  slot: InstrumentSlot,
  selectedBankId?: string,
): SamplerBankResult | null {
  const preset = slotPreset(state, slot);
  if (!preset) {
    return null;
  }
  const id = state.slotBankIds[slot] ?? selectedBankId;
  const entryIndex = id ? state.bank.findIndex(item => item.id === id) : -1;
  if (entryIndex < 0) {
    return null;
  }
  const next = cloneState(state);
  const entry = next.bank[entryIndex]!;
  entry.preset = cloneSamplerPreset({ ...preset, name: entry.name });
  next.instruments[slot] = samplerInstrument({ ...preset, name: entry.name });
  next.slotBankIds[slot] = entry.id;
  return {
    ...next,
    status: `Saved “${entry.name}”`,
    changedSlots: [slot],
  };
}

export function saveSamplerBankAs(
  state: SamplerBankState,
  slot: InstrumentSlot,
  name: string,
  overwrite: boolean,
): SamplerBankResult | { conflict: true } | null {
  const preset = slotPreset(state, slot);
  if (!preset) {
    return null;
  }
  const trimmed = name.trim();
  if (!trimmed) {
    return null;
  }
  const next = cloneState(state);
  const existing = next.bank.find(entry => entry.name === trimmed);
  if (existing && !overwrite) {
    return { conflict: true };
  }
  if (existing) {
    existing.preset = cloneSamplerPreset({ ...preset, name: trimmed });
    next.slotBankIds[slot] = existing.id;
    next.instruments[slot] = samplerInstrument({ ...preset, name: trimmed });
  } else {
    const id = newSamplerPresetId();
    next.bank.push({
      id,
      name: trimmed,
      builtin: false,
      category: "user",
      preset: cloneSamplerPreset({ ...preset, name: trimmed }),
    });
    next.slotBankIds[slot] = id;
    next.instruments[slot] = samplerInstrument({ ...preset, name: trimmed });
  }
  return {
    ...next,
    status: `Saved “${trimmed}”`,
    changedSlots: [slot],
  };
}

export function renameSamplerBankEntry(
  state: SamplerBankState,
  bankId: string,
  name: string,
): SamplerBankResult | { statusOnly: string } | null {
  const trimmed = name.trim();
  const entryIndex = state.bank.findIndex(item => item.id === bankId);
  if (entryIndex < 0) {
    return null;
  }
  const entry = state.bank[entryIndex]!;
  if (!trimmed || trimmed === entry.name) {
    return null;
  }
  if (state.bank.some(item => item.id !== entry.id && item.name === trimmed)) {
    return { statusOnly: `Name “${trimmed}” is already used` };
  }
  const next = cloneState(state);
  const target = next.bank[entryIndex]!;
  target.name = trimmed;
  target.preset = cloneSamplerPreset({ ...target.preset, name: trimmed });
  const changedSlots: InstrumentSlot[] = [];
  for (let i = 0; i < next.instruments.length; i++) {
    if (next.slotBankIds[i] === bankId && next.instruments[i]?.kind === "sampler") {
      const current = next.instruments[i]!;
      next.instruments[i] = samplerInstrument({ ...current.preset, name: trimmed });
      changedSlots.push(i as InstrumentSlot);
    }
  }
  return {
    ...next,
    status: `Renamed to “${trimmed}”`,
    changedSlots,
  };
}

export function deleteSamplerBankEntry(
  state: SamplerBankState,
  bankId: string,
): SamplerBankResult | { statusOnly: string } | null {
  const entry = state.bank.find(item => item.id === bankId);
  if (!entry) {
    return null;
  }
  if (entry.builtin) {
    return { statusOnly: "Factory presets cannot be deleted" };
  }
  const next = cloneState(state);
  next.bank = next.bank.filter(item => item.id !== bankId);
  for (let i = 0; i < next.slotBankIds.length; i++) {
    if (next.slotBankIds[i] === bankId) {
      next.slotBankIds[i] = next.bank[0]?.id;
    }
  }
  return {
    ...next,
    status: `Deleted “${entry.name}”`,
    changedSlots: [],
  };
}

export function setSamplerBankCategory(
  state: SamplerBankState,
  bankId: string,
  category: SamplerPresetCategoryId,
): SamplerBankResult | null {
  const entryIndex = state.bank.findIndex(item => item.id === bankId);
  if (entryIndex < 0) {
    return null;
  }
  const entry = state.bank[entryIndex]!;
  if (entry.builtin) {
    return null;
  }
  const nextCat = sanitizeSamplerPresetCategory(category, entry.category);
  if (entry.category === nextCat) {
    return null;
  }
  const next = cloneState(state);
  next.bank[entryIndex]!.category = nextCat;
  return {
    ...next,
    status: `Moved “${entry.name}” to ${samplerCategoryLabel(nextCat)}`,
    changedSlots: [],
  };
}

export function setSamplerBankTags(
  state: SamplerBankState,
  bankId: string,
  tagsRaw: string[],
): SamplerBankResult | null {
  const entryIndex = state.bank.findIndex(item => item.id === bankId);
  if (entryIndex < 0) {
    return null;
  }
  const next = cloneState(state);
  const entry = next.bank[entryIndex]!;
  const tags = sanitizeSamplerPresetTags(tagsRaw);
  if (tags) {
    entry.tags = tags;
  } else {
    delete entry.tags;
  }
  return {
    ...next,
    status: tags ? `Tagged “${entry.name}” · ${tags.join(", ")}` : `Cleared tags on “${entry.name}”`,
    changedSlots: [],
  };
}

export function buildSamplerExportEntryPayload(
  state: SamplerBankState,
  bankId: string | undefined,
  slot: InstrumentSlot,
): { json: string; filename: string; status: string } | null {
  const entry = bankId ? state.bank.find(item => item.id === bankId) : undefined;
  const live = slotPreset(state, slot);
  if (!entry && !live) {
    return null;
  }
  const payload = entry
    ? { type: "adam-sampler-preset", version: 1, preset: entry }
    : {
      type: "adam-sampler-preset",
      version: 1,
      preset: {
        id: newSamplerPresetId(),
        name: live!.name,
        category: "user" as const,
        preset: live!,
      },
    };
  const baseName = (entry?.name ?? live!.name) || "preset";
  const filename = `${baseName.replace(/\s+/g, "-")}.json`;
  return {
    json: JSON.stringify(payload, null, 2),
    filename,
    status: `Exported ${filename}`,
  };
}

export function buildSamplerExportAllPayload(state: SamplerBankState): {
  json: string;
  filename: string;
  status: string;
} {
  const bank = sanitizeSamplerBank(state.bank);
  return {
    json: JSON.stringify({ type: "adam-sampler-bank", version: 1, bank }, null, 2),
    filename: "adam-sampler-bank.json",
    status: `Exported ${bank.length} sampler presets`,
  };
}

export function importSamplerBankData(
  state: SamplerBankState,
  slot: InstrumentSlot,
  data: unknown,
): SamplerBankResult {
  if (!data || typeof data !== "object") {
    throw new Error("Invalid JSON");
  }
  const obj = data as Record<string, unknown>;

  if (
    (obj.type === "adam-sampler-bank" || obj.type === "curva-sampler-bank")
    && Array.isArray(obj.bank)
  ) {
    const next = cloneState(state);
    next.bank = sanitizeSamplerBank(obj.bank);
    const synced = syncSamplerSlotBankIdsFromInstruments(next);
    return {
      ...synced,
      status: `Imported sampler bank (${synced.bank.length} presets)`,
      changedSlots: [],
    };
  }

  if (
    (obj.type === "adam-sampler-preset" || obj.type === "curva-sampler-preset")
    && obj.preset
    && typeof obj.preset === "object"
  ) {
    const wrapped = obj.preset as Partial<NamedSamplerPreset>;
    if (wrapped.preset && isSamplerPresetShape(wrapped.preset)) {
      return addImportedNamed(state, slot, {
        id: typeof wrapped.id === "string" ? wrapped.id : newSamplerPresetId(),
        name: typeof wrapped.name === "string" ? wrapped.name : wrapped.preset.name || "Imported",
        builtin: false,
        category: sanitizeSamplerPresetCategory(wrapped.category, "user"),
        ...(sanitizeSamplerPresetTags(wrapped.tags)
          ? { tags: sanitizeSamplerPresetTags(wrapped.tags) }
          : {}),
        preset: cloneSamplerPreset(wrapped.preset),
      });
    }
  }

  if (isSamplerPresetShape(obj)) {
    const next = cloneState(state);
    next.instruments[slot] = samplerInstrument(obj);
    return {
      ...next,
      status: `Loaded preset into slot ${slot + 1} (Save as… to add to bank)`,
      changedSlots: [slot],
    };
  }

  if (Array.isArray(obj)) {
    const next = cloneState(state);
    next.bank = sanitizeSamplerBank([...next.bank, ...obj]);
    return {
      ...next,
      status: `Merged import into bank (${next.bank.length} presets)`,
      changedSlots: [],
    };
  }

  throw new Error("Unrecognized sampler preset JSON");
}

function addImportedNamed(
  state: SamplerBankState,
  slot: InstrumentSlot,
  entry: NamedSamplerPreset,
): SamplerBankResult {
  const next = cloneState(state);
  const existingIdx = next.bank.findIndex(
    item => item.id === entry.id || item.name === entry.name,
  );
  const tags = sanitizeSamplerPresetTags(entry.tags);
  let added: NamedSamplerPreset;
  if (existingIdx >= 0 && !next.bank[existingIdx]!.builtin) {
    added = {
      id: next.bank[existingIdx]!.id,
      name: entry.name,
      builtin: false,
      category: sanitizeSamplerPresetCategory(entry.category, next.bank[existingIdx]!.category),
      ...(tags ? { tags } : {}),
      preset: cloneSamplerPreset({ ...entry.preset, name: entry.name }),
    };
    next.bank[existingIdx] = added;
  } else {
    added = {
      id: newSamplerPresetId(),
      name: entry.name,
      builtin: false,
      category: sanitizeSamplerPresetCategory(entry.category, "user"),
      ...(tags ? { tags } : {}),
      preset: cloneSamplerPreset({ ...entry.preset, name: entry.name }),
    };
    next.bank.push(added);
  }
  next.slotBankIds[slot] = added.id;
  next.instruments[slot] = samplerInstrument({ ...added.preset, name: added.name });
  return {
    ...next,
    status: `Imported “${added.name}”`,
    changedSlots: [slot],
  };
}
